import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useUser } from '@/lib/auth';

import { useBuildFormContext } from '../../../form/schema';

/**
 * Quiz Title 기본 관리 Hook
 *
 * 퀴즈 제목의 읽기/쓰기/삭제 기능을 제공합니다.
 *
 * @example
 * const { title, titlePlaceholder, updateTitle, clearTitle } = useQuizTitle();
 *
 * // title 수동 업데이트
 * updateTitle('My Custom Title');
 *
 * // title 비우기
 * clearTitle();
 */
export const useQuizTitle = () => {
  const { setValue, control } = useBuildFormContext();
  const { user } = useUser();
  const { t } = useTranslation(['build']);

  // Watch current title
  const title = useWatch({ control, name: 'title' });

  // Title placeholder
  const titlePlaceholder = useMemo(() => {
    return t('build:titlePlaceholder', { username: user?.username });
  }, [user?.username, t]);

  // Manual title update function
  const updateTitle = useCallback(
    (value: string) => {
      setValue('title', value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    },
    [setValue],
  );

  // Clear title function
  const clearTitle = useCallback(() => {
    setValue('title', '', { shouldDirty: false });
  }, [setValue]);

  return {
    /** 현재 title 값 */
    title,
    /** Title placeholder 텍스트 */
    titlePlaceholder,
    /** Title을 수동으로 업데이트하는 함수 */
    updateTitle,
    /** Title을 비우는 함수 */
    clearTitle,
  };
};
